import { useState } from 'react';
import { Constants } from '../../../utils/constants';
import { EnterpriseEndpoints } from '../../../utils/endpoints';
import { formatNaira } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import { useServerTable, wholeDayDates } from '../../../hooks/useServerTable';
import PageHeader from '../../shell/PageHeader';
import { DataTable, DateRangeFilter, FilterSelect, Pagination, StatusPill, TableCard } from '../../ui';
import EnterpriseDeactivated, { isEnterpriseDeactivated } from './enterprise/EnterpriseDeactivated';
import StoreCell from './dashboard/StoreCell';
import StoreSelect from './enterprise/StoreSelect';
import useStores from './enterprise/useStores';

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Ledger' }];

const typeOptions = Object.entries(Constants.enterpriseLedgerTypes).map(([value, { label }]) => ({ value, label }));

const amountTone = {
  credit: 'whitespace-nowrap font-medium text-success-700',
  debit: 'whitespace-nowrap font-medium text-danger-700',
};

const sentenceCase = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '—');

const EntryType = ({ type }) => {
  const known = Constants.enterpriseLedgerTypes[type];
  return <StatusPill status={known?.label ?? type} tone={known?.tone} />;
};

const SignedAmount = ({ row }) => (
  <span className={amountTone[row.type]}>
    {row.type === 'debit' ? '-' : ''}
    {formatNaira(row.amount)}
  </span>
);

const EnterpriseLedger = () => {
  const stores = useStores();
  const [merchantId, setMerchantId] = useState('');
  const table = useServerTable(EnterpriseEndpoints.ledger, {
    filters: { type: '' },
    dateParams: wholeDayDates,
    extraParams: { merchant_id: merchantId },
    fallbackError: 'We could not load the ledger. Please try again.',
  });

  const header = (
    <>
      <PageHeader title="Ledger" breadcrumbs={breadcrumbs} />
      <p className="mb-6 text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
        The combined wallet statement of every store. Entries carry no running balance; the balance shown is the
        current total.
      </p>
    </>
  );

  if (isEnterpriseDeactivated(table.requestError)) {
    return (
      <>
        {header}
        <EnterpriseDeactivated />
      </>
    );
  }

  const columns = [
    {
      key: 'created_at',
      header: 'Date (WAT)',
      render: (row) => <span className="whitespace-nowrap">{formatLagos(row.created_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Date"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
    { key: 'store', header: 'Store', render: (row) => <StoreCell store={row.store} /> },
    { key: 'category', header: 'Category', render: (row) => sentenceCase(row.category) },
    { key: 'narration', header: 'Narration', render: (row) => row.narration || '—' },
    {
      key: 'type',
      header: 'Type',
      render: (row) => <EntryType type={row.type} />,
      filter: (
        <FilterSelect
          value={table.filters.type ?? ''}
          onChange={(value) => table.setFilter('type', value)}
          options={typeOptions}
          label="All"
          ariaLabel="Filter by type"
        />
      ),
    },
    { key: 'amount', header: 'Amount', align: 'right', render: (row) => <SignedAmount row={row} /> },
    {
      key: 'transaction_charges',
      header: 'Charges',
      align: 'right',
      render: (row) => formatNaira(row.transaction_charges),
    },
  ];

  return (
    <>
      {header}
      <TableCard
        title="Ledger"
        summary={{
          label: 'Balance',
          value: formatNaira(table.envelope?.total_balance),
          loading: table.loading,
        }}
        toolbar={stores.length > 0 && <StoreSelect stores={stores} value={merchantId} onChange={setMerchantId} />}
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(row) => row.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          empty={{ title: 'No ledger entries', message: 'No entries match the selected filters.' }}
        />
      </TableCard>
    </>
  );
};

export default EnterpriseLedger;
